import { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  FaPlus, FaArrowUp, FaArrowDown, FaBan, FaQuestionCircle, FaExclamationTriangle,
} from "react-icons/fa";
import Modal from "../Modal";
import OpcionesAdmin from "./OpcionesAdmin";

const TIPOS = [
  { value: "texto", label: "Texto" },
  { value: "si_no", label: "Sí/No" },
  { value: "checkbox", label: "Checkbox" },
];

export default function PreguntasAdmin({ seccionId }) {

  const { token } = useContext(AuthContext);

  const [preguntas, setPreguntas] = useState([]);
  const [texto, setTexto] = useState("");
  const [tipo, setTipo] = useState("texto");
  const [creando, setCreando] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [preguntaBaja, setPreguntaBaja] = useState(null);

  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });
  const mostrarToast = (mensaje, tipoToast = "success") => {
    setToast({ mostrar: true, mensaje, tipo: tipoToast });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const fetchPreguntas = async () => {
    if (!token) return;
    const res = await fetch(`/api/admin/preguntas/${seccionId}`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${token}` },
    });
    setPreguntas(await res.json());
  };

  useEffect(() => {
    fetchPreguntas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const crear = async () => {
    if (!texto.trim()) {
      mostrarToast("Escribe el texto de la pregunta", "error");
      return;
    }

    setCreando(true);
    try {
      const res = await fetch("/api/admin/preguntas", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ seccion_id: seccionId, texto, tipo, orden: preguntas.length + 1 }),
      });

      if (!res.ok) {
        mostrarToast("Error creando la pregunta", "error");
        return;
      }

      setTexto("");
      mostrarToast("Pregunta creada");
      fetchPreguntas();
    } catch (error) {
      mostrarToast("Error inesperado", "error");
    } finally {
      setCreando(false);
    }
  };

  const actualizar = async (p) => {
    try {
      const res = await fetch(`/api/admin/preguntas/${p.id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(p),
      });
      if (!res.ok) mostrarToast("Error guardando cambios", "error");
    } catch (error) {
      mostrarToast("Error inesperado", "error");
    }
  };

  const abrirConfirmBaja = (p) => {
    setPreguntaBaja(p);
    setConfirmOpen(true);
  };

  const confirmarBaja = async () => {
    await actualizar({ ...preguntaBaja, activo: false });
    mostrarToast("Pregunta desactivada");
    setConfirmOpen(false);
    fetchPreguntas();
  };

  const mover = async (index, dir) => {
    const nueva = [...preguntas];
    const temp = nueva[index];
    nueva[index] = nueva[index + dir];
    nueva[index + dir] = temp;

    setPreguntas(nueva);

    for (let i = 0; i < nueva.length; i++) {
      await actualizar({ ...nueva[i], orden: i + 1 });
    }
    fetchPreguntas();
  };

  const selectClass =
    "border border-slate-200 rounded-lg px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400";

  return (
    <div>
      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      {/* CREAR */}
      <div className="flex flex-wrap gap-2 mb-3">
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Nueva pregunta"
          className="flex-1 min-w-[160px] border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
        />

        <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={selectClass}>
          {TIPOS.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        <button
          onClick={crear}
          disabled={creando}
          className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium px-3.5 py-2 rounded-lg transition"
        >
          <FaPlus className="text-xs" /> Agregar
        </button>
      </div>

      {/* LISTA */}
      {preguntas.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">Aún no hay preguntas en esta sección.</p>
      ) : (
        <div className="space-y-2">
          {preguntas.map((p, index) => (
            <div key={p.id} className="border border-slate-200 rounded-lg p-2.5 bg-slate-50/60">
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-6 h-6 rounded-md bg-white border border-slate-200 text-slate-400 flex items-center justify-center shrink-0">
                  <FaQuestionCircle className="text-[10px]" />
                </span>

                <input
                  value={p.texto}
                  onChange={(e) => {
                    const nueva = [...preguntas];
                    nueva[index].texto = e.target.value;
                    setPreguntas(nueva);
                  }}
                  onBlur={() => actualizar(p)}
                  className="flex-1 min-w-[140px] border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />

                <select
                  value={p.tipo}
                  onChange={(e) => {
                    const nueva = [...preguntas];
                    nueva[index].tipo = e.target.value;
                    setPreguntas(nueva);
                    actualizar({ ...p, tipo: e.target.value });
                  }}
                  className={`${selectClass} py-1.5`}
                >
                  {TIPOS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>

                <div className="flex items-center gap-1 shrink-0">
                  <button
                    disabled={index === 0}
                    onClick={() => mover(index, -1)}
                    className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 disabled:opacity-30 hover:bg-slate-50 transition"
                    title="Subir"
                  >
                    <FaArrowUp className="text-[10px]" />
                  </button>
                  <button
                    disabled={index === preguntas.length - 1}
                    onClick={() => mover(index, 1)}
                    className="w-7 h-7 flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 disabled:opacity-30 hover:bg-slate-50 transition"
                    title="Bajar"
                  >
                    <FaArrowDown className="text-[10px]" />
                  </button>
                  <button
                    onClick={() => abrirConfirmBaja(p)}
                    className="w-7 h-7 flex items-center justify-center rounded-md border border-rose-200 bg-white text-rose-500 hover:bg-rose-50 transition"
                    title="Desactivar"
                  >
                    <FaBan className="text-[10px]" />
                  </button>
                </div>
              </div>

              {p.tipo === "checkbox" && (
                <div className="mt-2 pl-8">
                  <OpcionesAdmin preguntaId={p.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* MODAL CONFIRMAR DESACTIVAR */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="max-w-sm">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 bg-rose-50 text-rose-500">
            <FaExclamationTriangle />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Desactivar pregunta</h3>
          <p className="text-sm text-slate-500 mb-6">¿Deseas continuar?</p>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmOpen(false)}
              className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={confirmarBaja}
              className="w-full px-4 py-2 text-sm rounded-lg text-white font-medium transition bg-rose-600 hover:bg-rose-700"
            >
              Confirmar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
